import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import App from './App'
import './index.css'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import { supabase } from './lib/supabaseClient'
import { setupPushNotifications } from './utils/pushNotifications'
import { initializeReminders } from './utils/reminderNotifications'

// Create QueryClient with caching configuration for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 min
      gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for 30 min (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch if data exists in cache
    },
  },
})
const root = document.getElementById('root')!

const app = (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TooltipProvider>
          <App />
        </TooltipProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </HelmetProvider>
)

if (Capacitor.isNativePlatform()) {
  // Set up push notifications (FCM) - but only register, don't save token yet
  setupPushNotifications().catch((err) => {
    console.error('[Push] setup error', err)
  })
  
  // Initialize local notification reminders
  initializeReminders().catch((err) => {
    console.error('[Reminders] setup error', err)
  })

  // Listen for app state changes to refresh session and ensure connectivity
  CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
    if (isActive) {
      console.log('[Capacitor] App became active, refreshing session...');
      try {
        // First, get the current session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log('[Capacitor] Session found, refreshing token...');
          // Refresh the session to ensure the token is valid
          const { data, error } = await supabase.auth.refreshSession();
          
          if (error) {
            console.error('[Capacitor] Session refresh error:', error);
            // If refresh fails, the session might be invalid
            // The auth state change listener will handle this
          } else if (data.session) {
            console.log('[Capacitor] Session refreshed successfully');
          }
        } else {
          console.log('[Capacitor] No session found on app resume');
        }
      } catch (err) {
        console.error('[Capacitor] Error checking session:', err);
      }
    }
  });

  CapacitorApp.addListener('appUrlOpen', async ({ url }) => {
    try {
      console.log('[Capacitor] appUrlOpen received:', url);

      // Normalize the custom scheme to parse with URL
      const normalizedUrl = url.replace('metefy://', 'https://')
      const parsed = new URL(normalizedUrl)
      const fragment = parsed.hash || ''
      const query = parsed.search || ''

      console.log('[Capacitor] Fragment:', fragment);
      console.log('[Capacitor] Query:', query);

      // Try fragment first (access_token/refresh_token)
      const fragmentParams = new URLSearchParams(fragment.startsWith('#') ? fragment.slice(1) : fragment)
      const accessToken = fragmentParams.get('access_token')
      const refreshToken = fragmentParams.get('refresh_token')

      if (accessToken && refreshToken) {
        console.log('[Capacitor] Setting session with tokens');
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        
        if (error) {
          console.error('[Capacitor] Session error:', error);
          return;
        }
        
        if (data.session) {
          console.log('[Capacitor] Session established successfully');
          // Use router navigation instead of window.location
          // This will be handled by the auth state change listener
          // Force a small delay to ensure session is saved
          setTimeout(() => {
            window.location.href = '/profile';
          }, 100);
        }
        return;
      }

      // Fallback for PKCE/code flow (query param ?code=)
      const queryParams = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query)
      const code = queryParams.get('code')
      if (code) {
        console.log('[Capacitor] Exchanging code for session');
        const { data, error } = await supabase.auth.exchangeCodeForSession(code)
        if (error) {
          console.error('[Capacitor] Code exchange error:', error);
        } else if (data.session) {
          console.log('[Capacitor] Session established, navigating to profile');
          setTimeout(() => {
            window.location.href = '/profile';
          }, 100);
        }
      } else {
        console.warn('[Capacitor] No access_token, refresh_token, or code found in URL');
      }
    } catch (error) {
      console.error('[Capacitor] Failed to handle appUrlOpen', error)
    }
  })
}

// Register service worker for offline support (production only)
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('[Service Worker] Registered successfully:', registration.scope);
        
        // Check for updates every hour
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);
      })
      .catch((error) => {
        console.error('[Service Worker] Registration failed:', error);
      });
  });
}

// Use hydration for SSR in production
if (import.meta.env.PROD) {
  hydrateRoot(root, app)
} else {
  createRoot(root).render(app)
}
